import * as XLSX from "xlsx";
import {
  AddProductRaw,
  productsUpdatesValidator,
  RemoveProductRaw,
  SwapProductRaw,
  UploadError,
} from "./validators";
import { ResponseGetMerchflow_merchflowId_FlowUpdateProducts } from "@CommonApi/merchflow/_merchflowId_/flow-update/products";
import { PayloadPostMerchflows_merchflowId_StoresValidate } from "@CommonApi/merchflows/_merchflowId_/stores/validate";
import { ProductUpdates } from "./types";
import { downloadBlob } from "src/utils/downloadFile";

export const SHEET_SWAP_PRODUCTS = "SWAP PRODUCTS";
export const SHEET_ADD_PRODUCTS = "ADD PRODUCTS";
export const SHEET_REMOVE_PRODUCTS = "REMOVE PRODUCTS";

export interface UploadSummary {
  swapCount: number;
  addCount: number;
  removeCount: number;
  errors: UploadError[];
}

/**
 * Parse store codes from raw XLSX data.
 * Handles various types that XLSX might return (string, number, null, undefined).
 */
export const parseStoreCodesFromRaw = (storesCodes?: string | number | null): string[] => {
  if (!storesCodes) return [];
  return String(storesCodes || "")
    .split(",")
    .map((code) => code.trim())
    .filter((code) => code !== "");
};

/**
 * Generate example XLSX template with product data.
 * Uses real products from the provided data when available, falls back to hardcoded examples.
 */
export const downloadExampleXlsx = (
  products: ResponseGetMerchflow_merchflowId_FlowUpdateProducts,
): void => {
  const workbook = XLSX.utils.book_new();

  // Helper to get swap products data
  const getSwapProductsData = (): SwapProductRaw[] => {
    const removable = products.removableProducts.slice(0, 2);
    const addable = products.addableProducts.slice(0, 2);

    if (removable.length >= 1 && addable.length >= 1) {
      return [
        {
          remove_product_code: removable[0]?.productCode ?? "",
          pnl_decision: "SALES",
          add_product_code: addable[0]?.productCode ?? "",
          swap_facings_mode: "MATCH",
          stores_codes: "2911, 2938",
        },
        ...(removable.length >= 2 && addable.length >= 2
          ? [
              {
                remove_product_code: removable[1]?.productCode ?? "",
                pnl_decision: "DEFAULT",
                add_product_code: addable[1]?.productCode ?? "",
                swap_facings_mode: "DOS_MOS_BALANCE",
                stores_codes: "",
              },
            ]
          : []),
      ];
    }

    // Fallback to hardcoded examples
    return [
      {
        remove_product_code: "524617",
        pnl_decision: "SALES",
        add_product_code: "827814, 941537",
        swap_facings_mode: "MATCH",
        stores_codes: "2911, 2938",
      },
      {
        remove_product_code: "989822",
        pnl_decision: "DEFAULT",
        add_product_code: "941537",
        swap_facings_mode: "DOS_MOS_BALANCE",
        stores_codes: "",
      },
    ];
  };

  // Helper to get add products data
  const getAddProductsData = (): AddProductRaw[] => {
    const addable = products.addableProducts.slice(0, 3);

    if (addable.length > 0) {
      const examples: AddProductRaw[] = [];
      addable.forEach((product, index) => {
        examples.push({
          add_product_code: product.productCode,
          fit_strategy:
            index === 0
              ? "CAN_DELETE_RANGED_PRODUCTS"
              : index === 1
                ? "KEEP_ALL_FACINGS"
                : "CAN_REDUCE_FACINGS",
          pnl_decision: index === 0 ? "SALES" : index === 1 ? "MOVEMENT" : "PROFIT",
          facings: index === 2 ? "2" : "AUTO",
          ...(index === 2 && {
            place_to: "LEFT",
            adjacent_product_code: products.removableProducts[0]?.productCode ?? "",
          }),
          stores_codes: index === 0 ? "" : index === 1 ? "2911, 2938" : "2911",
        });
      });
      return examples;
    }

    // Fallback to hardcoded examples
    return [
      {
        add_product_code: "1851",
        fit_strategy: "CAN_DELETE_RANGED_PRODUCTS",
        pnl_decision: "SALES",
        facings: "AUTO",
        stores_codes: "2911, 2938",
      },
      {
        add_product_code: "1847",
        fit_strategy: "KEEP_ALL_FACINGS",
        pnl_decision: "MOVEMENT",
        facings: "AUTO",
        stores_codes: "",
      },
      {
        add_product_code: "1850",
        fit_strategy: "CAN_REDUCE_FACINGS",
        pnl_decision: "PROFIT",
        place_to: "LEFT",
        adjacent_product_code: "524617",
        facings: "2",
        stores_codes: "2911",
      },
    ];
  };

  // Helper to get remove products data
  const getRemoveProductsData = (): RemoveProductRaw[] => {
    const removable = products.removableProducts.slice(0, 3);

    if (removable.length > 0) {
      const examples: RemoveProductRaw[] = [];
      removable.forEach((product, index) => {
        examples.push({
          remove_product_code: product.productCode,
          pnl_decision: index === 0 ? "SALES" : "DEFAULT",
          stores_codes: index === 0 ? "2911, 2938" : "",
        });
      });
      return examples;
    }

    // Fallback to hardcoded examples
    return [
      {
        remove_product_code: "640346",
        pnl_decision: "SALES",
        stores_codes: "2911, 2938",
      },
      {
        remove_product_code: "777852",
        pnl_decision: "DEFAULT",
        stores_codes: "",
      },
    ];
  };

  // SWAP PRODUCTS sheet
  const swapProductsData = getSwapProductsData();
  const swapProductsSheet = XLSX.utils.json_to_sheet(swapProductsData, {
    header: [
      "remove_product_code",
      "pnl_decision",
      "add_product_code",
      "swap_facings_mode",
      "stores_codes",
    ],
  });
  XLSX.utils.book_append_sheet(workbook, swapProductsSheet, SHEET_SWAP_PRODUCTS);

  // ADD PRODUCTS sheet
  const addProductsData = getAddProductsData();
  const addProductsSheet = XLSX.utils.json_to_sheet(addProductsData, {
    header: [
      "add_product_code",
      "place_to",
      "adjacent_product_code",
      "facings",
      "reducible_product_code",
      "fit_strategy",
      "pnl_decision",
      "stores_codes",
    ],
  });
  XLSX.utils.book_append_sheet(workbook, addProductsSheet, SHEET_ADD_PRODUCTS);

  // REMOVE PRODUCTS sheet
  const removeProductsData = getRemoveProductsData();
  const removeProductsSheet = XLSX.utils.json_to_sheet(removeProductsData, {
    header: ["remove_product_code", "pnl_decision", "stores_codes"],
  });
  XLSX.utils.book_append_sheet(workbook, removeProductsSheet, SHEET_REMOVE_PRODUCTS);

  // Write to file
  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  }) as ArrayBuffer;
  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  downloadBlob(blob, "product-updates-template.xlsx");
};

export interface UploadProductsUpdatesFileParams {
  file: File;
  merchflowId: number;
  products: ResponseGetMerchflow_merchflowId_FlowUpdateProducts;
  validateStores: (
    merchflowId: number,
    payload: PayloadPostMerchflows_merchflowId_StoresValidate,
  ) => Promise<{ valid_stores: string[]; invalid_stores: string[] } | null>;
  onError: (error: { title: string; error?: unknown; type?: string }) => void;
}

/**
 * Process uploaded XLSX file and return product updates and summary.
 * Returns null if processing fails.
 */
export const uploadProductsUpdatesFile = async (
  params: UploadProductsUpdatesFileParams,
): Promise<{ productsUpdates: ProductUpdates; summary: UploadSummary } | null> => {
  const { file, merchflowId, products, validateStores, onError } = params;

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onerror = (event) => {
      onError({
        title:
          "An error occurred while reading the spreadsheet file. Please check the file format and try again.",
        error: event,
      });
      resolve(null);
    };
    reader.onload = async (event) => {
      if (!event.target?.result) {
        onError({ title: "Failed to read spreadsheet file", type: "ERROR" });
        resolve(null);
        return;
      }

      const data = XLSX.read(event.target.result);

      if (!data.Sheets[SHEET_SWAP_PRODUCTS]) {
        onError({ title: "Swap products sheet missing from the spreadsheet" });
        resolve(null);
        return;
      }
      if (!data.Sheets[SHEET_ADD_PRODUCTS]) {
        onError({ title: "Add products sheet missing from the spreadsheet" });
        resolve(null);
        return;
      }
      if (!data.Sheets[SHEET_REMOVE_PRODUCTS]) {
        onError({ title: "Remove products sheet missing from the spreadsheet" });
        resolve(null);
        return;
      }

      const isEmptyRow = (row: object): boolean =>
        Object.values(row).every((v) => v === undefined || v === null || v === "");

      const swapProducts = XLSX.utils
        .sheet_to_json<SwapProductRaw>(data.Sheets[SHEET_SWAP_PRODUCTS])
        .filter((row) => !isEmptyRow(row));
      const addProducts = XLSX.utils
        .sheet_to_json<AddProductRaw>(data.Sheets[SHEET_ADD_PRODUCTS])
        .filter((row) => !isEmptyRow(row));
      const removeProducts = XLSX.utils
        .sheet_to_json<RemoveProductRaw>(data.Sheets[SHEET_REMOVE_PRODUCTS])
        .filter((row) => !isEmptyRow(row));

      // Collect all unique store codes from all sheets
      const allStoreCodes = new Set<string>();
      [...swapProducts, ...addProducts, ...removeProducts].forEach((row) => {
        const storeCodes = parseStoreCodesFromRaw(row.stores_codes);
        storeCodes.forEach((code) => allStoreCodes.add(code));
      });

      // Validate stores if there are any store codes
      let availableStoreCodes: Set<string> | undefined;
      if (allStoreCodes.size > 0) {
        const validationResponse = await validateStores(merchflowId, {
          store_codes: Array.from(allStoreCodes),
        });

        if (!validationResponse) {
          onError({
            title: "Failed to validate stores. Please try again.",
            type: "ERROR",
          });
          resolve(null);
          return;
        }

        availableStoreCodes = new Set(validationResponse.valid_stores);
      }

      const { productsUpdates: newProductsUpdates, errors } = productsUpdatesValidator(
        products,
        swapProducts,
        addProducts,
        removeProducts,
        availableStoreCodes,
      );

      // Calculate counts in a single pass
      let swapCount = 0;
      let addCount = 0;
      let removeCount = 0;
      for (const productUpdate of newProductsUpdates) {
        if (productUpdate.type === "swap") swapCount++;
        else if (productUpdate.type === "add") addCount++;
        else if (productUpdate.type === "remove") removeCount++;
      }

      const summary: UploadSummary = {
        swapCount,
        addCount,
        removeCount,
        errors,
      };

      resolve({
        productsUpdates: newProductsUpdates,
        summary,
      });
    };
    reader.readAsArrayBuffer(file);
  });
};
